import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import CustomAlert from "./CustomAlert";
import "./SignIn.css";
import logo from "../assets/xtrafyls.svg";
import { google_icon, apple_icon, emailIcon, passwordIcon } from "../assets";
import { loginUser, googleLogin, appleLogin, clearError } from "../features/auth/authSlice";

// Apple Login Configuration - Replace with actual values from Apple Developer Account
const APPLE_CLIENT_ID = "com.xtrafyl.web"; // TODO: Replace with actual Service ID
// const APPLE_REDIRECT_URI = "https://staging.xtrafyl.com/auth/apple/callback"; // Or specific callback staging URL
const APPLE_REDIRECT_URI = "https://xtrafyl.com/auth/apple/callback"; // Or specific callback prod URL

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required")
    .matches(
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      "Please enter a valid email address"
    ),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters"),
});

const SignIn = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isLoading, error } = useSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [showApprovedPopup, setShowApprovedPopup] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    type: "success",
    title: "",
    message: "",
  });

  const showCustomAlert = (type, title, message) => {
    setAlertConfig({ type, title, message });
    setShowAlert(true);
  };

  const initialValues = {
    email: "",
    password: "",
  };

  useEffect(() => {
    if (error) {
      showCustomAlert("error", "Sign In Error", error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handlePostLoginNavigation = (resultPayload) => {
    const user = resultPayload?.User || resultPayload?.user;
    const profile = resultPayload?.Profile || resultPayload?.profile;

    const profileStep =
      user?.ProfileStep ||
      user?.profileStep ||
      profile?.ProfileStep ||
      profile?.profileStep;
    const basicInfoCompleted =
      user?.BasicInfoCompleted ??
      profile?.BasicInfoCompleted ??
      user?.basicInfoCompleted ??
      profile?.basicInfoCompleted ??
      false;
    const documentsCompleted =
      user?.DocumentsCompleted ??
      profile?.DocumentsCompleted ??
      user?.documentsCompleted ??
      profile?.documentsCompleted ??
      profile?.Documents ??
      false;
    const verificationStatus =
      profile?.VerificationStatus || profile?.verificationStatus || "unknown";

    // Check if backend wants to show welcome popup (only for first time after approval)
    const showPopup =
      resultPayload?.ShowPopup ??
      resultPayload?.showPopup ??
      profile?.ShowPopup ??
      profile?.showPopup ??
      false;

    if (profileStep === "set_password") {
      navigate("/create-password");
    } else if (!basicInfoCompleted) {
      navigate("/business-profile");
    } else if (!documentsCompleted) {
      navigate("/business-profile");
    } else if (basicInfoCompleted && documentsCompleted) {
      if (verificationStatus === "pending") {
        navigate("/verification-pending");
      } else if (
        verificationStatus === "verified" ||
        verificationStatus === "approved"
      ) {
        // ShowPopup: true means show the popup, false means go directly to dashboard
        if (showPopup) {
          setShowApprovedPopup(true);
        } else {
          navigate("/dashboard");
        }
      } else {
        navigate("/verification-pending");
      }
    } else {
      navigate("/dashboard");
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      localStorage.setItem("userEmail", values.email);

      const resultAction = await dispatch(
        loginUser({ email: values.email, password: values.password })
      );

      if (loginUser.fulfilled.match(resultAction)) {
        handlePostLoginNavigation(resultAction.payload);
      }
    } catch (error) {
      showCustomAlert(
        "error",
        "Login Error",
        "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const resultAction = await dispatch(
        googleLogin(credentialResponse.credential)
      );

      if (googleLogin.fulfilled.match(resultAction)) {
        const user = resultAction.payload?.User || resultAction.payload?.user;

        if (user?.Email || user?.email) {
          const email = user.Email || user.email;
          localStorage.setItem("userEmail", email);
        }

        handlePostLoginNavigation(resultAction.payload);
      } else if (googleLogin.rejected.match(resultAction)) {
        showCustomAlert(
          "error",
          "Google Login Error",
          resultAction.payload || "Login failed. Please try again."
        );
      }
    } catch (error) {
      showCustomAlert(
        "error",
        "Google Login Error",
        "Something went wrong. Please try again."
      );
    }
  };

  const handleGoogleError = () => {
    showCustomAlert(
      "error",
      "Google Login Failed",
      "Unable to login with Google. Please try again."
    );
  };

  const handleAppleLogin = async () => {
    try {
      // Load Apple JS SDK if not loaded
      if (!window.AppleID) {
        const script = document.createElement("script");
        script.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
        script.async = true;
        document.body.appendChild(script);
        await new Promise((resolve) => {
          script.onload = resolve;
        });
      }

      // Initialize Apple Sign In
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: "name email",
        redirectURI: APPLE_REDIRECT_URI,
        usePopup: true,
      });

      // Trigger Apple Sign In
      const response = await window.AppleID.auth.signIn();
      const { authorization, user } = response;

      const resultAction = await dispatch(
        appleLogin({
          idToken: authorization.id_token,
          authorizationCode: authorization.code,
          user: user || null,
        })
      );

      if (appleLogin.fulfilled.match(resultAction)) {
        const userData = resultAction.payload?.User || resultAction.payload?.user;
        if (userData?.Email || userData?.email) {
          localStorage.setItem("userEmail", userData.Email || userData.email);
        }

        // Check if it's a new user - redirect to registration flow
        const isNewUser = resultAction.payload?.IsNewUser ?? resultAction.payload?.isNewUser ?? resultAction.payload?.is_new_user ?? false;
        if (isNewUser) {
          navigate("/create-password");
          return;
        }

        handlePostLoginNavigation(resultAction.payload);
      } else if (appleLogin.rejected.match(resultAction)) {
        showCustomAlert(
          "error",
          "Apple Login Error",
          resultAction.payload || "Login failed. Please try again."
        );
      }
    } catch (error) {
      if (error.error !== "popup_closed_by_user") {
        showCustomAlert(
          "error",
          "Apple Login Failed",
          "Unable to login with Apple. Please try again."
        );
      }
    }
  };

  return (
    <div className="signin-container">
      <div className="signin-left">
        <div className="left-content">
          <img src={logo} alt="Xtrafyl" className="logo" />
          <div className="hero-section">
            <h1 className="hero-title">
              Fuel Your Business
              <br />
              With <span className="highlight">Xtrafyl.</span>
            </h1>
            <p className="hero-description text-big-body">
              From fuel delivery to charging, car wash, and more — we help you
              reach more customers, every day.
            </p>
          </div>
        </div>
      </div>

      <div className="signin-right">
        <div className="signin-right-content">
          <div className="form-header">
            <h2>Sign In</h2>
            <p className="text-small-body">
              Welcome back! Continue growing your business with Xtrafyl's
              provider platform.
            </p>
          </div>

          <div className="form-container">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validateOnChange={true}
              validateOnBlur={true}
            >
              {({ errors, touched, isSubmitting, isValid, dirty }) => (
                <Form className="signin-form">
                  <div className="form-group">
                    <label htmlFor="email" className="text-label">
                      Email Id
                    </label>
                    <div className="input-wrapper">
                      <img src={emailIcon} alt="" className="input-icon" />
                      <Field
                        type="email"
                        id="email"
                        name="email"
                        placeholder="Email Id"
                        className={`text-small-body ${
                          errors.email && touched.email ? "input-error" : ""
                        } ${
                          !errors.email && touched.email ? "input-success" : ""
                        }`}
                      />
                      {!errors.email && touched.email && (
                        <svg
                          className="input-success-icon"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M16.6667 5L7.50004 14.1667L3.33337 10"
                            stroke="#10B981"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="error-message"
                    />
                  </div>

                  <div className="form-group signin">
                    <label htmlFor="password" className="text-label">
                      Password
                    </label>
                    <div className="input-wrapper">
                      <img src={passwordIcon} alt="" className="input-icon" />
                      <Field
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        placeholder="Password"
                        className={`text-small-body ${
                          errors.password && touched.password
                            ? "input-error"
                            : ""
                        } ${
                          !errors.password && touched.password
                            ? "input-success"
                            : ""
                        }`}
                      />
                      <button
                        type="button"
                        className="toggle-password"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label="Toggle password visibility"
                      >
                        {showPassword ? (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <path
                              d="M2.5 10C2.5 10 5 4.16667 10 4.16667C15 4.16667 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z"
                              stroke="#64748B"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z"
                              stroke="#64748B"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <path
                              d="M14.95 14.95C13.5255 16.0358 11.7904 16.6374 10 16.6667C5 16.6667 2.5 10.8333 2.5 10.8333C3.35558 9.06831 4.52744 7.48604 5.95 6.16667M8.25 4.53333C8.82361 4.39907 9.41083 4.33195 10 4.33333C15 4.33333 17.5 10.1667 17.5 10.1667C17.0125 11.0756 16.4208 11.9272 15.7367 12.7033M11.7667 11.7667C11.5379 12.0123 11.2617 12.2093 10.9545 12.3459C10.6474 12.4826 10.316 12.556 9.97979 12.562C9.64359 12.568 9.30979 12.5063 8.99812 12.3806C8.68645 12.2549 8.40348 12.0675 8.16627 11.8303C7.92906 11.5931 7.7417 11.3101 7.61598 10.9985C7.49027 10.6868 7.42859 10.353 7.43458 10.0168C7.44057 9.68062 7.51409 9.34921 7.65074 9.04206C7.78738 8.73491 7.98442 8.45868 8.23 8.23"
                              stroke="#64748B"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M2.5 2.5L17.5 17.5"
                              stroke="#64748B"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="error-message"
                    />
                  </div>

                  <div className="forgot-password">
                    <span
                      onClick={() => navigate("/forgot-password")}
                      className="text-small-body"
                      style={{ cursor: "pointer", color: "#6366F1" }}
                    >
                      Forgot Password?
                    </span>
                  </div>

                  <button
                    type="submit"
                    className="signin-button text-button"
                    disabled={isSubmitting || isLoading || !isValid || !dirty}
                  >
                    {isSubmitting || isLoading ? "SIGNING IN..." : "SIGN IN"}
                  </button>
                </Form>
              )}
            </Formik>

            <div className="divider">
              <span className="text-small-body">Or</span>
            </div>

            <div className="social-buttons">
              <div className="social-button-wrapper">
                <button className="social-button text-small-body-medium">
                  <img src={google_icon} alt="Google" className="social-icon" />
                  Continue with Google
                </button>
                <div className="google-login-overlay">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    useOneTap={false}
                    type="standard"
                    theme="outline"
                    size="large"
                    text="continue_with"
                    shape="rectangular"
                    width="300"
                  />
                </div>
              </div>
              <div className="social-button-wrapper">
                <button
                  className="social-button text-small-body-medium apple-login-btn"
                  onClick={handleAppleLogin}
                >
                  <img src={apple_icon} alt="Apple" className="social-icon" />
                  Continue with Apple
                </button>
              </div>
            </div>

            <p className="signup-link text-small-body">
              Don't have an account?{" "}
              <span
                onClick={() => navigate("/signup")}
                style={{ cursor: "pointer", color: "#6366F1", fontWeight: 600 }}
              >
                Sign Up
              </span>
            </p>
          </div>
        </div>
      </div>

      {showApprovedPopup && (
        <div
          className="signin-popup-overlay"
          onClick={() => {
            setShowApprovedPopup(false);
            navigate("/dashboard");
          }}
        >
          <div
            className="signin-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="popup-close-btn"
              onClick={() => {
                setShowApprovedPopup(false);
                navigate("/dashboard");
              }}
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                background: "transparent",
                border: "none",
                fontSize: "24px",
                color: "#64748B",
                cursor: "pointer",
                padding: "4px",
                lineHeight: "1",
              }}
            >
              ✕
            </button>

            <div className="signin-popup-icon">
              <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
                <circle
                  cx="60"
                  cy="60"
                  r="60"
                  fill="#10B981"
                  fillOpacity="0.1"
                />
                <circle cx="60" cy="60" r="48" fill="#10B981" />
                <path
                  d="M37.5 60L52.5 75L82.5 45"
                  stroke="white"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h3 className="signin-popup-title">Profile Approved!</h3>
            <p className="signin-popup-message">
              Congratulations! Your profile has been verified and approved.
              Welcome to Xtrafyl dashboard.
            </p>

            <button
              className="signin-dashboard-button"
              onClick={() => {
                setShowApprovedPopup(false);
                navigate("/dashboard");
              }}
            >
              GO TO DASHBOARD
            </button>
          </div>
        </div>
      )}

      <CustomAlert
        show={showAlert}
        onClose={() => setShowAlert(false)}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
      />
    </div>
  );
};

export default SignIn;
